import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, CreditCard, Gift, Info, Layers } from "lucide-react";
import { toast } from "sonner";

import { ChalkActionButton, ChalkCard, SectionTitle } from "../components/chalk/primitives";
import { tbilling, tournaments, usd } from "../lib/tournamentApi";

/** Upgrade screen: band table, today's charge, plain-language true-up, consent, then checkout. */
export default function TournamentUpgrade() {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [q, setQ] = useState(null);
  const [kind, setKind] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    tournaments.get(id).then((d) => setT(d.tournament)).catch((e) => toast.error(e.message));
    tbilling.event(id).then((d) => {
      setQ(d);
      setKind(d.plan?.available ? "plan_event" : d.free_event.admin_grant ? "admin_grant" : d.free_event.eligible ? "free_event" : "hub_event");
    }).catch((e) => toast.error(e.message));
  }, [id]);
  if (!q || !t) return <div style={{ padding: 20 }}>Loading…</div>;
  if (q.tier === "hub") return <AlreadyHub id={id} />;
  const back = `${window.location.origin}/app/tournaments/${id}`;
  const options = buildOptions(q);
  const opt = options.find((o) => o.kind === kind) || options[0];

  async function go() {
    setBusy(true);
    try {
      if (opt.kind === "admin_grant") { await tbilling.claimGrant(id); nav(`/app/tournaments/${id}?upgraded=1`); return; }
      const r = await tbilling.upgrade(id, { kind: opt.kind, consent, consent_version: q.consent_version,
                                             success_url: `${back}?upgraded=1`, cancel_url: `${back}/upgrade` });
      if (r.activated) { nav(`/app/tournaments/${id}?upgraded=1`); return; }
      window.location.href = r.checkout_url;
    } catch (e) { toast.error(e.message || "Couldn't start checkout."); }
    finally { setBusy(false); }
  }
  return (
    <div data-testid="upgrade-page" style={{ maxWidth: 640, margin: "0 auto", padding: "16px 16px 80px" }}>
      <Link to={`/app/tournaments/${id}`} data-testid="upgrade-back" style={{ fontSize: 13, color: "var(--chalk-muted)", display: "inline-flex", gap: 4, alignItems: "center", textDecoration: "none" }}>
        <ArrowLeft size={14} /> {t.name}</Link>
      <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 40, margin: "10px 0 4px", lineHeight: 1 }}>Upgrade to The Tournament Hub</h1>
      <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginBottom: 16 }}>Divisions, pools, double elimination, check-in, Big Screen, results graphics and fan alerts.</div>
      {q.balance_due_cents > 0 && <BalanceBanner cents={q.balance_due_cents} back={back} />}
      <BandTable q={q} />
      {q.contact_required ? (
        <ChalkCard data-testid="upgrade-contact" style={{ marginTop: 14 }}>65+ teams is a custom plan. <a href="mailto:support@getchalkos.com?subject=65%2B%20team%20tournament">Contact us</a> and we'll set it up.</ChalkCard>
      ) : (
        <>
          <SectionTitle label="How do you want to pay?" />
          {options.map((o) => (
            <button key={o.kind} data-testid={`upgrade-option-${o.kind}`} disabled={o.disabled} onClick={() => setKind(o.kind)}
                    style={{ ...optStyle, borderColor: opt.kind === o.kind ? "var(--green-mid)" : "var(--chalk-border)", opacity: o.disabled ? 0.5 : 1 }}>
              <o.icon size={18} color="var(--green-mid)" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>{o.title}</div>
                <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 2 }}>{o.sub}</div>
              </div>
              <div style={{ fontWeight: 800 }}>{o.today}</div>
            </button>
          ))}
          <Link to="/app/director" data-testid="upgrade-director-link" style={{ fontSize: 12, color: "var(--green-mid)", fontWeight: 700 }}>
            Running several events a year? See Director plans →</Link>
          <ChalkCard data-testid="upgrade-today" style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 700 }}>Charged today</div>
            <div data-testid="upgrade-today-amount" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 34 }}>{opt.today}</div>
          </ChalkCard>
          <ChalkCard data-testid="upgrade-trueup" style={{ marginTop: 10, display: "flex", gap: 10 }}>
            <Info size={18} color="var(--green-mid)" aria-hidden="true" style={{ flexShrink: 0 }} />
            <div style={{ fontSize: 13, lineHeight: 1.55 }}>{opt.trueup}</div>
          </ChalkCard>
          {opt.kind !== "admin_grant" && (
            <label data-testid="upgrade-consent" style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, lineHeight: 1.5, margin: "16px 0", cursor: "pointer" }}>
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="upgrade-consent-checkbox" style={{ marginTop: 3, width: 18, height: 18 }} />
              <span>{q.consent_text} <a href="/terms#tournament-billing" target="_blank" rel="noreferrer" data-testid="upgrade-terms-link">Pricing terms</a></span>
            </label>
          )}
          <ChalkActionButton label={busy ? "One moment…" : opt.cta} testId="upgrade-continue" disabled={busy || opt.disabled || (opt.kind !== "admin_grant" && !consent) || q.balance_due_cents > 0}
                             onClick={go} icon={<Check size={16} />} />
        </>
      )}
    </div>
  );
}

function buildOptions(q) {
  const today = usd(q.today_cents);
  const out = [];
  if (q.plan) {
    out.push({ kind: "plan_event", icon: Layers, title: `Use a ${q.plan.label} event`, today: "$0", disabled: !q.plan.available, cta: "Use plan event",
               sub: q.plan.limit ? `${q.plan.used} of ${q.plan.limit} used this year${q.plan.reserved ? ` · ${q.plan.reserved} reserved` : ""}${q.plan.available ? "" : " · limit reached"}` : "Unlimited events",
               trueup: q.plan.plan === "director_plus" ? "Covered up to 64 teams. Nothing is charged after the event for team count."
                 : "Covered up to 32 teams. If 33–64 teams end up checking in or playing, we'll email you and charge the $80 difference 48 hours after the event. The event counts toward your 6 only once it goes live." });
  }
  if (q.free_event.admin_grant) {
    out.push({ kind: "admin_grant", icon: Gift, title: "Your free first Hub event (granted)", today: "$0", cta: "Turn on Hub, free",
               sub: "Set up by the ChalkOS team. No card needed.", trueup: "Free for up to 32 teams. If it finishes with 33–64 teams, we'll email you a $80 balance to pay." });
  }
  out.push({ kind: "free_event", icon: Gift, title: "Free first Hub event", today: "$0", disabled: !q.free_event.eligible || q.free_event.admin_grant, cta: "Save card and turn on Hub",
             sub: q.free_event.eligible ? "One per director. A card on file is required; nothing is charged today." : "Already used on this account.",
             trueup: "Free for up to 32 teams. We save your card now and charge nothing today. If 33–64 teams check in or play, we'll email you and charge the $80 difference 48 hours after the event." });
  out.push({ kind: "hub_event", icon: CreditCard, title: "Hub for this event", today, cta: `Pay ${today} and turn on Hub`,
             sub: `${q.registered_teams} team${q.registered_teams === 1 ? "" : "s"} registered now`,
             trueup: `You pay for the band your ${q.registered_teams} registered teams fall in today. Nothing ever stops mid-event. Afterwards we count teams that checked in or played: if it's a bigger band we email you the difference and charge it 48 hours later; if it's smaller we refund the difference automatically. More than 3 helpers is $5 each, added to that bill.` });
  return out;
}

function BandTable({ q }) {
  return (
    <ChalkCard data-testid="upgrade-bands" style={{ padding: 0, overflow: "hidden" }}>
      {q.bands.map((b) => {
        const on = b.key === q.current_band;
        return (
          <div key={b.key} data-testid={`upgrade-band-${b.key}`} style={{ display: "flex", justifyContent: "space-between", padding: "12px 14px",
                                                                          background: on ? "rgba(46,125,82,0.08)" : "transparent", borderBottom: "0.5px solid var(--chalk-border)" }}>
            <span style={{ fontWeight: on ? 800 : 500 }}>{b.label}{on && <span style={{ color: "var(--green-mid)", fontSize: 11, marginLeft: 8 }}>YOUR EVENT TODAY</span>}</span>
            <span style={{ fontWeight: 800 }}>{usd(b.cents)}</span>
          </div>
        );
      })}
      <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 14px", color: "var(--chalk-muted)" }}>
        <span>65+ teams</span><span>Contact us</span></div>
    </ChalkCard>
  );
}

export function BalanceBanner({ cents, back }) {
  async function pay() {
    try {
      const r = await tbilling.balanceCheckout({ success_url: `${back}?balance=1`, cancel_url: back });
      window.location.href = r.checkout_url;
    } catch (e) { toast.error(e.message); }
  }
  return (
    <ChalkCard data-testid="balance-banner" style={{ marginBottom: 14, borderLeft: "3px solid #C0392B" }}>
      <div style={{ fontWeight: 700 }}>You have a {usd(cents)} balance from a past event.</div>
      <div style={{ fontSize: 12, color: "var(--chalk-muted)", margin: "4px 0 10px" }}>Live and past events keep working. Pay it to upgrade new events.</div>
      <ChalkActionButton label={`Pay ${usd(cents)}`} testId="balance-pay" fullWidth={false} onClick={pay} />
    </ChalkCard>
  );
}

function AlreadyHub({ id }) {
  return (
    <div data-testid="upgrade-already" style={{ padding: 24 }}>This tournament is already on the Hub. <Link to={`/app/tournaments/${id}`}>Back</Link></div>
  );
}

const optStyle = { display: "flex", gap: 12, alignItems: "flex-start", width: "100%", textAlign: "left", background: "#fff", cursor: "pointer",
                   border: "1.5px solid", borderRadius: 12, padding: "12px 14px", marginBottom: 8, font: "inherit", color: "inherit" };
